import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, Chip, Collapse, Grid, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { ExpandMore as ExpandMoreIcon, FilterList as FilterIcon, RestartAlt as ResetIcon, Search as SearchIcon, Tune as TuneIcon } from "@mui/icons-material";
import { IFASReportFilter } from "../../../../../services/ifasReportsService";
import { ifasColors } from "../../components/OperationsUI";

interface Props {
  filter: IFASReportFilter;
  onFilterChange: (newFilter: IFASReportFilter) => void;
  onReset: () => void;
  reportContext?: string;
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px", backgroundColor: ifasColors.surface, minHeight: 44,
    "& fieldset": { borderColor: ifasColors.border },
    "&:hover fieldset": { borderColor: ifasColors.muted },
    "&.Mui-focused fieldset": { borderColor: ifasColors.orange, borderWidth: 2 },
  },
  "& .MuiInputLabel-root": { color: ifasColors.muted, fontWeight: 500 },
};

const formatLocalDate = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const IFASReportFilterBar: React.FC<Props> = ({ filter, onFilterChange, onReset, reportContext }) => {
  const [draft, setDraft] = useState<IFASReportFilter>(filter);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => setDraft(filter), [filter]);

  const activeCount = useMemo(
    () => Object.values(filter).filter((value) => value !== undefined && value !== null && value !== "").length,
    [filter]
  );
  const isMortuary = reportContext === "mortuary";
  const isFuneral = reportContext === "funeral";
  const isServices = reportContext === "services";
  const statusOptions = isMortuary
    ? [["PENDING_INTAKE", "Pending intake"], ["INTAKE_REGISTERED", "Intake registered"], ["BODY_CONTROL_ACTIVE", "Body control active"], ["TRANSFER_IN_PROGRESS", "Transfer in progress"], ["RELEASED_TO_EXTERNAL_PARLOUR", "Released to external parlour"], ["DELIVERED_TO_FAMILY", "Delivered to family"]]
    : [["ACTIVE", "Active"], ["COMPLETED", "Completed"], ["CANCELLED", "Cancelled"], ["SCHEDULED", "Scheduled"]];
  const periodHelp = reportContext === "mortuary"
    ? "Period applies to mortuary admissions, releases and trend dates. Current occupancy remains a live snapshot."
    : reportContext === "services"
      ? "Period applies to the funeral, service or installation date."
      : reportContext === "fleetWarehouse"
        ? "Period applies to allocation, removal, preparation and inventory movement dates."
        : reportContext === "funeral"
          ? "Period applies to the scheduled funeral date."
          : "Period is applied to each report's relevant business date.";

  const handleChange = (field: keyof IFASReportFilter, value: string) => {
    setDraft((current) => ({ ...current, [field]: value || undefined }));
  };

  const handleQuickDatePreset = (preset: "today" | "week" | "month" | "all") => {
    const end = new Date();
    const start = new Date(end);
    if (preset === "week") start.setDate(start.getDate() - 7);
    if (preset === "month") start.setDate(start.getDate() - 30);
    const next = {
      ...draft,
      startDate: preset === "all" ? undefined : formatLocalDate(start),
      endDate: preset === "all" ? undefined : formatLocalDate(end),
    };
    setDraft(next);
    onFilterChange(next);
  };

  const handleReset = () => {
    setDraft({});
    setAdvancedOpen(false);
    onReset();
  };

  return (
    <Card elevation={0} sx={{ mb: 2.5, borderRadius: "16px", border: `1px solid ${ifasColors.border}`, background: ifasColors.surface, boxShadow: "0 12px 34px rgba(15,23,42,0.09)", overflow: "visible" }}>
      <Box sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", lg: "center" }} spacing={1.5} sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box sx={{ width: 38, height: 38, borderRadius: "11px", bgcolor: ifasColors.softOrange, display: "grid", placeItems: "center" }}>
              <FilterIcon sx={{ color: ifasColors.orange, fontSize: 20 }} />
            </Box>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: ifasColors.text }}>Filter reports</Typography>
                {activeCount > 0 && <Chip label={`${activeCount} active`} size="small" sx={{ height: 22, bgcolor: ifasColors.softOrange, color: ifasColors.deepOrange, fontWeight: 700 }} />}
              </Stack>
              <Typography sx={{ fontSize: "0.75rem", color: ifasColors.muted }}>{periodHelp}</Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ overflowX: "auto", pb: { xs: 0.5, lg: 0 } }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: ifasColors.muted, whiteSpace: "nowrap", mr: 0.5 }}>DATE RANGE</Typography>
            {[["Today", "today"], ["7 days", "week"], ["30 days", "month"], ["All time", "all"]].map(([label, value]) => (
              <Chip key={value} label={label} size="small" clickable onClick={() => handleQuickDatePreset(value as "today" | "week" | "month" | "all")}
                sx={{ flexShrink: 0, height: 30, borderRadius: "8px", bgcolor: ifasColors.page, border: `1px solid ${ifasColors.border}`, color: ifasColors.text, fontWeight: 700, "&:hover": { bgcolor: ifasColors.softOrange, borderColor: ifasColors.orange, color: ifasColors.deepOrange } }} />
            ))}
          </Stack>
        </Stack>

        <Grid container spacing={1.5} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}><TextField fullWidth size="small" label="Report period from" type="date" InputLabelProps={{ shrink: true }} value={draft.startDate || ""} onChange={(e) => handleChange("startDate", e.target.value)} sx={fieldSx} /></Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}><TextField fullWidth size="small" label="Report period to" type="date" InputLabelProps={{ shrink: true }} value={draft.endDate || ""} onChange={(e) => handleChange("endDate", e.target.value)} sx={fieldSx} /></Grid>
          {isFuneral && <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <TextField fullWidth select size="small" label="Client / funeral type" value={draft.funeralType || ""} onChange={(e) => handleChange("funeralType", e.target.value)} sx={fieldSx}>
              <MenuItem value="">All client / funeral types</MenuItem>
              <MenuItem value="CASH">Cash Client</MenuItem>
              <MenuItem value="SCHEME">Scheme Client</MenuItem>
              <MenuItem value="SCHEME_TOP_UP">Scheme Top-up</MenuItem>
              <MenuItem value="MASS">Mass Funeral</MenuItem>
              <MenuItem value="STATE">State Funeral</MenuItem>
              <MenuItem value="SUBCONTRACTED">Subcontracted</MenuItem>
            </TextField>
          </Grid>}
          {isServices && <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <TextField fullWidth select size="small" label="Service category" value={draft.funeralType || ""} onChange={(e) => handleChange("funeralType", e.target.value)} sx={fieldSx}>
              <MenuItem value="">All service categories</MenuItem>
              <MenuItem value="CATERING">Catering</MenuItem>
              <MenuItem value="TOMBSTONE">Tombstone</MenuItem>
              <MenuItem value="SPECIAL SERVICE">Special Service</MenuItem>
            </TextField>
          </Grid>}
          {isMortuary && <Grid size={{ xs: 12, sm: 6, lg: 2 }}><TextField fullWidth size="small" label="Mortuary / Branch" value={draft.branch || ""} onChange={(e) => handleChange("branch", e.target.value)} sx={fieldSx} /></Grid>}
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <TextField fullWidth select size="small" label="Status" value={draft.status || ""} onChange={(e) => handleChange("status", e.target.value)} sx={fieldSx}>
              <MenuItem value="">All statuses</MenuItem>{statusOptions.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <Button fullWidth variant="outlined" startIcon={<TuneIcon />} endIcon={<ExpandMoreIcon sx={{ transform: advancedOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />} onClick={() => setAdvancedOpen((open) => !open)}
              aria-expanded={advancedOpen}
              sx={{
                height: 44,
                borderRadius: "10px",
                borderColor: `${advancedOpen ? ifasColors.orange : ifasColors.border} !important`,
                color: `${advancedOpen ? ifasColors.orange : ifasColors.text} !important`,
                backgroundColor: `${advancedOpen ? ifasColors.softOrange : ifasColors.surface} !important`,
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 600,
                boxShadow: "none !important",
                "&:hover": {
                  borderColor: `${ifasColors.orange} !important`,
                  color: `${ifasColors.orange} !important`,
                  backgroundColor: `${ifasColors.softOrange} !important`,
                  boxShadow: "none !important",
                },
                "&:focus-visible": {
                  borderColor: `${ifasColors.orange} !important`,
                  backgroundColor: `${ifasColors.softOrange} !important`,
                  boxShadow: "0 0 0 3px rgba(236,110,0,0.14) !important",
                },
              }}>More filters</Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <Button fullWidth variant="contained" startIcon={<SearchIcon />} onClick={() => onFilterChange(draft)}
              sx={{ height: 44, borderRadius: "10px", bgcolor: ifasColors.orange, color: "#FFF !important", textTransform: "none", fontSize: "0.82rem", boxShadow: "0 6px 14px rgba(236,110,0,.22)", "&:hover": { bgcolor: ifasColors.deepOrange } }}>Apply filters</Button>
          </Grid>
        </Grid>

        <Collapse in={advancedOpen}>
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${ifasColors.border}` }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}><TextField fullWidth size="small" label="Province" value={draft.province || ""} onChange={(e) => handleChange("province", e.target.value)} sx={fieldSx} /></Grid>
              {!isMortuary && <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}><TextField fullWidth size="small" label="Branch" value={draft.branch || ""} onChange={(e) => handleChange("branch", e.target.value)} sx={fieldSx} /></Grid>}
              <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}><TextField fullWidth size="small" label="Operations centre" value={draft.operationsCentre || ""} onChange={(e) => handleChange("operationsCentre", e.target.value)} sx={fieldSx} /></Grid>
              {(isFuneral || isServices) && <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}><TextField fullWidth size="small" label={isServices ? "Planner / consultant" : "Funeral planner"} value={draft.plannerName || ""} onChange={(e) => handleChange("plannerName", e.target.value)} sx={fieldSx} /></Grid>}
              {(isFuneral || isServices) && <Grid size={{ xs: 12, sm: 6, lg: 2.4 }}><TextField fullWidth size="small" label={isServices ? "Exact service date" : "Exact funeral date"} type="date" InputLabelProps={{ shrink: true }} value={draft.funeralDate || ""} onChange={(e) => handleChange("funeralDate", e.target.value)} sx={fieldSx} /></Grid>}
            </Grid>
          </Box>
        </Collapse>

        {(activeCount > 0 || Object.values(draft).some(Boolean)) && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
            <Button size="small" startIcon={<ResetIcon />} onClick={handleReset} sx={{ color: `${ifasColors.muted} !important`, textTransform: "none", fontSize: "0.75rem", lineHeight: 1.8 }}>Clear all filters</Button>
          </Stack>
        )}
      </Box>
    </Card>
  );
};
